import { Controller } from "@hotwired/stimulus"

// The public link's answer box (Phelipe 2026-10-09): a name (unless signed
// in), words, pictures and files — presign → S3 PUT, the house chips — then
// Send lands it on the request as a comment, straight into the thread here.
export default class extends Controller {
  static targets = ["name", "body", "chips", "error", "file", "send", "thread"]
  static values = { presignUrl: String, replyUrl: String, name: String, sent: String, failed: String }

  pick() { this.fileTarget.click() }

  filesChosen(e) {
    for (const f of e.currentTarget.files) this._add(f)
    e.currentTarget.value = ""
  }

  _add(file) {
    const entry = { name: file.name, size: file.size, type: file.type, status: "uploading" }
    ;(this.files ||= []).push(entry)
    entry.promise = this._upload(entry, file)
    this._paint()
  }

  async _upload(entry, file) {
    try {
      const pre = await fetch(this.presignUrlValue, { method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: entry.name, content_type: entry.type || "application/octet-stream", byte_size: entry.size }] }) })
      if (!pre.ok) throw new Error((await pre.json().catch(() => ({}))).error || this.failedValue)
      const up = (await pre.json()).uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: file })
      if (!put.ok) throw new Error(this.failedValue)
      entry.cache_name = up.cache_name; entry.status = "done"
    } catch (e) {
      entry.status = "failed"; this._error(e.message)
    }
    this._paint()
  }

  _paint() {
    this.chipsTarget.innerHTML = (this.files || []).map((f, i) => {
      const up = f.status === "uploading", bad = f.status === "failed"
      return `<span class="inline-flex items-center gap-1.5 max-w-full rounded-full border px-2.5 py-0.5 text-[12.5px] font-medium ${up ? "bg-blue-50 text-blue-800 border-blue-200" : bad ? "bg-red-50 text-red-800 border-red-200" : "bg-gray-100 text-gray-700 border-gray-200"}">
        ${up ? `<span class="w-3 h-3 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin"></span>` : ""}<span class="truncate max-w-[200px]">${this._esc(f.name)}</span>
        <button type="button" class="px-1 leading-none opacity-70 hover:opacity-100 cursor-pointer" data-rm="${i}" aria-label="Remove">×</button></span>`
    }).join("")
    this.chipsTarget.querySelectorAll("[data-rm]").forEach(b => b.onclick = () => { this.files.splice(+b.dataset.rm, 1); this._paint() })
  }

  async send() {
    const name = this.hasNameTarget ? this.nameTarget.value.trim() : this.nameValue
    const body = this.bodyTarget.value.trim()
    const files = this.files || []
    this.errorTarget.classList.add("hidden")
    this.sendTarget.disabled = true
    await Promise.all(files.filter(f => f.status === "uploading").map(f => f.promise))
    const done = files.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.name, content_type: f.type, byte_size: f.size }))
    const res = await fetch(this.replyUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify({ name, body, files: done }) }).catch(() => null)
    const data = res ? await res.json().catch(() => ({})) : {}
    this.sendTarget.disabled = false
    if (!res || !res.ok) return this._error(data.error || this.failedValue)
    this.threadTarget.querySelector("[data-empty]")?.remove()
    this.threadTarget.insertAdjacentHTML("beforeend", data.comment_html)
    this.threadTarget.lastElementChild?.scrollIntoView({ block: "nearest" })
    this.bodyTarget.value = ""; this.files = []; this._paint()
    this._toast(this.sentValue)
  }

  _error(msg) { this.errorTarget.textContent = msg; this.errorTarget.classList.remove("hidden") }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
  _headers() { return { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" } }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
